import React, { FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { ReactComponent as YandexIcon } from 'shared/assets/icons/YandexIcon.svg';
import { Button } from 'shared/ui/Button';
import { useYandexAuthMutation } from '../api/yandexAuthApi';
import { getBoxInfo, getWarehouseInfo } from 'pages/BookingPage';
import { LeadData, useCreateLeadMutation } from 'entities/Lead';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { CLIENT_HOST, YANDEX_AUTH_CLIENT_ID } from 'app/config/envVariables';

const width = 500;
const height = 600;
const left = window.screenX + (window.outerWidth - width) / 2;
const top = window.screenY + (window.outerHeight - height) / 2;
const windowFeatures = `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes`;

interface YandexAuthButtonProps extends PropsWithChildren {
  onSuccessAuth: () => void;
  onFailAuth: (error: string) => void;
  className?: string;
}

export const YandexAuthButton: FC<YandexAuthButtonProps> = (props) => {
  const { children, className, onSuccessAuth, onFailAuth } = props;
  const { t } = useAppTranslation('booking');
  const [yandexLogin] = useYandexAuthMutation();
  const [createLead] = useCreateLeadMutation();

  const warehouseData = useAppSelector(getWarehouseInfo);
  const boxData = useAppSelector(getBoxInfo);

  const handleYandexAuth = useCallback(() => {
    const redirectUri = `${CLIENT_HOST}/login-process`;

    const oauthUrl = `https://oauth.yandex.ru/authorize?response_type=token&client_id=${YANDEX_AUTH_CLIENT_ID}&redirect_uri=${redirectUri}&force_confirm=yes`;

    window.open(oauthUrl, '_blank', windowFeatures);
  }, []);

  const handleMessage = useCallback(
    async (event: MessageEvent): Promise<void> => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const token = event.data?.access_token;

      if (!token) {
        return;
      }

      try {
        const response = await yandexLogin(token).unwrap();

        if (response?.email) {
          const leadData: LeadData = {
            email: response.email,
            square: boxData?.sizeCode?.square,
            warehouseId: warehouseData?.warehouseId,
            note: t('Lead signed in with Yandex'),
            isThirdPartyAuthorized: true,
          };

          await createLead(leadData);
        }

        onSuccessAuth();
      } catch (err: CustomAny) {
        onFailAuth(err?.data?.message || t('Yandex login failed'));
      }
    },
    [boxData?.sizeCode?.square, createLead, onFailAuth, onSuccessAuth, t, warehouseData?.warehouseId, yandexLogin],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [yandexLogin, createLead, boxData, warehouseData, t, onSuccessAuth, onFailAuth, handleMessage]);

  return (
    <Button
      size="medium-large"
      containerClassName={className}
      iconPosition="prev"
      icon={<YandexIcon className="w-[30px] h-[30px]" />}
      onClick={handleYandexAuth}
      className="!bg-[#FC3F1D] !px-6"
    >
      {children}
    </Button>
  );
};
